<template>
	<ClientTeleport>
		<Transition name="fade">
			<div v-if="visible" ref="popupRef" class="@container/popup fixed z-popup inset-0 grid items-end h-full bg-[#333]/60 backdrop-blur-xs" v-bind="$attrs">
				<div class="max-h-screen pt-20 grid items-end h-[100dvh] mx-auto w-full sm:container lg:items-start lg:pt-32 ">
					<div
						ref="popupBodyRef"
						class="relative flex flex-col bg-[#F8F8F8] @popup-md/popup:bg-background-primary sm:rem:rounded-t-[20px] w-full max-h-full min-h-0 shadow-lg md:rounded-xl max-md:min-h-[30dvh]"
					>
						<div class="flex items-center justify-between p-4 md:p-6 relative">
							<slot name="headerLeft" />
							<button
								class="absolute top-3.5 right-4 md:top-6 md:right-6 text-icon-primary hover:bg-button-secondary-surface-hover rounded-full transition ease"
								type="button"
								aria-label="Zavřít"
								@click="close"
							>
								<NuxtIcon name="x" filled class="text-2xl" />
							</button>
						</div>
						<div
							class="shrink-1 grow-0 max-h-full min-h-0 px-4 pb-4 md:px-6 md:pb-6 h-full"
							:class="customOverflow ? 'flex flex-col' : 'overflow-auto overscroll-contain'"
						>
							<slot v-bind="slotProps" />
						</div>
					</div>
				</div>
			</div>
		</Transition>
	</ClientTeleport>
</template>

<script lang="ts" setup>
	import {onClickOutside} from '@vueuse/core';
	import {useFocusTrap} from '@vueuse/integrations/useFocusTrap';
	import {storeToRefs} from 'pinia';
	import {useSelectboxStore} from '~/stores/selectbox';

	type Props = {
		visible: boolean;

		// Set this prop to true if the popup message content handles overflow and scrolling on its own.
		// For example when it makes sense to scroll only inside a table in the content instead of scrolling
		// the entire content.
		customOverflow?: boolean;
		customBodyClasses?: string;
	};

	interface Emits {
		(e: 'update:visible', payload: boolean): void
	}

	const props = defineProps<Props>();
	const emit = defineEmits<Emits>();

	const popup = useTemplateRef('popupRef');
	const popupBody = useTemplateRef('popupBodyRef');
	const slotProps = computed(() => ({close}));

	const isScrollLocked = useBodyScrollLock();
	const {activate: activateFocusTrap, deactivate: deactivateFocusTrap} = useFocusTrap(popup);

	const {selectboxDropdown} = storeToRefs(useSelectboxStore());

	onClickOutside(popupBody, close, {ignore: [selectboxDropdown]});

	onKeyStroke('Escape', close);

	onBeforeUnmount(() => {
		deactivateFocusTrap();
	});

	watch(() => props.visible, async (newValue) => {
		isScrollLocked.value = newValue;
		await nextTick();
		newValue ? activateFocusTrap() : deactivateFocusTrap();
	}, {immediate: true});

	function close() {
		emit('update:visible', false);
	}
</script>
